/**
 * Quản lý tài khoản (Doc/03 mục 7) — chỉ admin.
 *
 * Không có nút xoá tài khoản: `submissions.enumerator_id` là FK RESTRICT, xoá người
 * dùng sẽ hoặc bị chặn, hoặc (nếu ai đó nới ràng buộc) làm mọi phiếu họ từng nhập
 * mất dấu vết người thực hiện. Khoá tài khoản mới là thao tác đúng — người đã nghỉ
 * không đăng nhập được nữa nhưng lịch sử phiếu vẫn nguyên vẹn.
 *
 * Mật khẩu chỉ đi một chiều: nhập vào khi tạo hoặc khi đặt lại, không bao giờ đọc
 * ngược ra được. Server hash bằng argon2id và không trả `passwordHash` trong bất kỳ
 * response nào (farmer-auth-rbac §2).
 */
import * as api from '../api-client.js';
import { el, esc, toast, formatDateTime } from '../ui/util.js';
import { createRegionPicker } from '../ui/region-picker.js';

const ROLE_LABEL = {
  enumerator: 'Điều tra viên',
  supervisor: 'Điều phối viên',
  instance_admin: 'Quản trị đơn vị',
  admin: 'Quản trị hệ thống',
};

const ROLE_HINT = {
  enumerator: 'Nhập và nộp phiếu của chính mình, trong địa bàn được gán.',
  supervisor: 'Như điều tra viên, cộng duyệt/từ chối/mở lại phiếu trong địa bàn phụ trách.',
  instance_admin:
    'Toàn bộ đơn vị: quản lý tài khoản, hộ/vườn, duyệt phiếu, xuất dữ liệu — ' +
    'nhưng KHÔNG sửa bộ câu hỏi, mục đối chiếu hay ngưỡng (dùng chung toàn hệ thống).',
  admin: 'Toàn hệ thống, mọi đơn vị: tài khoản, bộ câu hỏi, mục đối chiếu, đơn vị điều tra.',
};

/** Hai vai trò này làm việc theo địa bàn — không gán thì đăng nhập vào chỗ trống. */
const NEEDS_REGION = new Set(['enumerator', 'supervisor']);

const roleOptions = (selected, allowed = Object.keys(ROLE_LABEL)) =>
  allowed
    .map(
      (v) =>
        `<option value="${v}"${v === selected ? ' selected' : ''}>${esc(ROLE_LABEL[v])}</option>`,
    )
    .join('');

export async function renderUsers(container) {
  const me = api.getCurrentUser();
  // Quản trị đơn vị dùng CÙNG màn hình này, chỉ khác phạm vi: máy chủ trả về đúng
  // tài khoản trong đơn vị của họ (R7). Không tự lọc ở đây — ẩn nút không phải là
  // phân quyền.
  const laAdminHeThong = me?.role === 'admin';
  if (!me || (me.role !== 'admin' && me.role !== 'instance_admin')) {
    container.innerHTML = `<div class="empty-state"><span class="ms ms-xl">lock</span>
      <p>Chỉ quản trị hệ thống hoặc quản trị đơn vị mới được quản lý tài khoản.</p></div>`;
    return;
  }

  /**
   * Vai trò được phép cấp. Chỉ admin hệ thống cấp được vai trò admin hệ thống —
   * server chặn bằng `assertCanGrantRole`, đây chỉ là không bày ra thứ sẽ bị từ chối.
   */
  const vaiTroCapDuoc = laAdminHeThong
    ? Object.keys(ROLE_LABEL)
    : ['enumerator', 'supervisor', 'instance_admin'];

  /** Danh sách đơn vị — chỉ admin hệ thống cần, vì họ phải chọn đơn vị khi tạo. */
  let donVis = [];
  if (laAdminHeThong) {
    try {
      donVis = await api.instances.list();
    } catch (err) {
      console.warn('[users] không tải được danh sách đơn vị', err);
    }
  }

  container.innerHTML = `
    <div class="page-head">
      <div>
        <h1>Tài khoản</h1>
        <p>Tạo tài khoản, gán vai trò và địa bàn phụ trách. Người đã nghỉ thì <b>khoá</b>
           chứ không xoá — lịch sử phiếu họ đã nhập cần giữ nguyên người thực hiện.</p>
      </div>
      <div class="page-head-actions">
        <button class="btn" id="uNewBtn"><span class="ms">person_add</span>Tạo tài khoản</button>
      </div>
    </div>

    <div class="filters-bar">
      <input type="search" id="uSearch" class="search-field" placeholder="Tìm theo họ tên hoặc tên đăng nhập..." aria-label="Tìm tài khoản">
      <select id="uRole" aria-label="Lọc theo vai trò">
        <option value="">Tất cả vai trò</option>
        ${roleOptions('', vaiTroCapDuoc)}
      </select>
      ${
        laAdminHeThong
          ? `<select id="uInstance" aria-label="Lọc theo đơn vị điều tra">
               <option value="">Tất cả đơn vị</option>
               ${donVis.map((d) => `<option value="${esc(d.id)}">${esc(d.name)}</option>`).join('')}
             </select>`
          : ''
      }
      <select id="uStatus" aria-label="Lọc theo trạng thái">
        <option value="all">Tất cả trạng thái</option>
        <option value="active">Đang hoạt động</option>
        <option value="inactive">Đã khoá</option>
      </select>
    </div>

    <div id="uList"></div>`;

  const listHost = container.querySelector('#uList');
  const searchInput = container.querySelector('#uSearch');
  const roleSelect = container.querySelector('#uRole');
  const statusSelect = container.querySelector('#uStatus');
  const instanceSelect = container.querySelector('#uInstance');

  let users = [];

  async function load() {
    listHost.innerHTML = `<div class="empty-state">Đang tải danh sách tài khoản...</div>`;
    try {
      users = await api.users.list({
        search: searchInput.value.trim() || undefined,
        role: roleSelect.value || undefined,
        instanceId: instanceSelect?.value || undefined,
        status: statusSelect.value,
      });
      renderList();
    } catch (err) {
      listHost.innerHTML = `<div class="empty-state"><span class="ms ms-xl">error</span>
        <p>${esc(err.message)}</p></div>`;
    }
  }

  function renderList() {
    if (!users.length) {
      listHost.innerHTML = `<div class="empty-state"><span class="ms ms-xl">person_off</span>
        <p>Không có tài khoản nào khớp bộ lọc.</p></div>`;
      return;
    }
    listHost.innerHTML = `
      <div class="card">
        <table class="list-table">
          <thead><tr>
            <th>Họ tên</th><th>Tên đăng nhập</th><th>Vai trò</th>
            ${laAdminHeThong ? '<th>Đơn vị</th>' : ''}<th>Địa bàn</th>
            <th>Trạng thái</th><th>Đăng nhập lần cuối</th><th></th>
          </tr></thead>
          <tbody>
            ${users
              .map((u) => {
                const self = u.id === me.id;
                return `<tr class="${u.isActive ? '' : 'user-locked'}">
                  <td>${esc(u.fullName)}${self ? ' <span class="badge badge-self">bạn</span>' : ''}</td>
                  <td><code>${esc(u.username)}</code></td>
                  <td>${esc(ROLE_LABEL[u.role] || u.role)}</td>
                  ${laAdminHeThong ? `<td>${esc(u.instanceName || '—')}</td>` : ''}
                  <td>${esc(u.regionName || '—')}</td>
                  <td><span class="badge ${u.isActive ? 'user-active' : 'user-inactive'}">${u.isActive ? 'Hoạt động' : 'Đã khoá'}</span></td>
                  <td>${u.lastLoginAt ? esc(formatDateTime(u.lastLoginAt)) : '<span class="hint-text">chưa đăng nhập</span>'}</td>
                  <td class="user-actions">
                    <button class="btn btn-text sm" data-act="edit" data-id="${esc(u.id)}">Sửa</button>
                    <button class="btn btn-text sm" data-act="pwd" data-id="${esc(u.id)}">Đặt lại mật khẩu</button>
                    ${
                      self
                        ? ''
                        : `<button class="btn btn-text sm ${u.isActive ? 'danger' : ''}" data-act="toggle" data-id="${esc(u.id)}">${u.isActive ? 'Khoá' : 'Mở khoá'}</button>`
                    }
                  </td>
                </tr>`;
              })
              .join('')}
          </tbody>
        </table>
      </div>`;

    listHost.querySelectorAll('button[data-act]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const user = users.find((u) => u.id === btn.dataset.id);
        if (!user) return;
        if (btn.dataset.act === 'edit') openEditDialog(user);
        if (btn.dataset.act === 'pwd') openPasswordDialog(user);
        if (btn.dataset.act === 'toggle') toggleActive(user);
      });
    });
  }

  async function toggleActive(user) {
    const next = !user.isActive;
    const warn = next
      ? `Mở khoá tài khoản "${user.fullName}"?`
      : `Khoá tài khoản "${user.fullName}"? Họ sẽ bị đăng xuất ngay và không đăng nhập lại được.`;
    if (!window.confirm(warn)) return;
    try {
      await api.users.update(user.id, { isActive: next });
      toast(next ? 'Đã mở khoá tài khoản' : 'Đã khoá tài khoản và thu hồi phiên đăng nhập');
      await load();
    } catch (err) {
      toast('Không thực hiện được: ' + err.message);
    }
  }

  // ------------------------------------------------------------- hộp thoại
  /**
   * `onSubmit` là hàm async trả về chuỗi lỗi để hiện trong hộp thoại, hoặc không
   * trả gì khi thành công (lúc đó hộp thoại tự đóng). Khác bản trong form-builder
   * ở chỗ có await — ở đây mỗi lần bấm Lưu đều là một request thật.
   */
  function openDialog({ title, bodyHtml, submitLabel, onSubmit, onMount }) {
    const backdrop = el('div', 'dialog-backdrop');
    const form = el('form', 'dialog');
    form.noValidate = true;
    form.innerHTML = `
      <div class="dialog-head"><h3>${esc(title)}</h3>
        <button class="icon-btn" type="button" data-act="close" aria-label="Đóng"><span class="ms">close</span></button></div>
      <div class="dialog-body">${bodyHtml}</div>
      <div class="dialog-error error-text" hidden></div>
      <div class="dialog-actions">
        <button class="btn btn-text" type="button" data-act="cancel">Huỷ</button>
        <button class="btn" type="submit"><span class="ms">save</span>${esc(submitLabel)}</button>
      </div>`;
    backdrop.appendChild(form);
    document.body.appendChild(backdrop);

    const close = () => backdrop.remove();
    form.querySelector('[data-act="close"]').addEventListener('click', close);
    form.querySelector('[data-act="cancel"]').addEventListener('click', close);
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    backdrop.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });

    const errBox = form.querySelector('.dialog-error');
    const submitBtn = form.querySelector('button[type="submit"]');
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      errBox.hidden = true;
      submitBtn.disabled = true;
      try {
        const err = await onSubmit(form);
        if (err) {
          errBox.textContent = err;
          errBox.hidden = false;
          return;
        }
        close();
      } catch (err) {
        // Lỗi ngoài dự kiến (bug trong onSubmit) phải hiện ra ngay tại hộp thoại.
        // Không có nhánh này thì Promise bị treo im lặng và nút Lưu trông như hỏng:
        // bấm không ra gì, không báo gì — đúng lỗi đã gặp ngày 2026-08-08.
        console.error('[users] lỗi khi lưu hộp thoại', err);
        errBox.textContent = err?.message || 'Đã xảy ra lỗi không xác định.';
        errBox.hidden = false;
      } finally {
        submitBtn.disabled = false;
      }
    });

    onMount?.(form);
    form.querySelector('input, select')?.focus();
    return { close, form };
  }

  /** Ô vai trò + ô địa bàn đi liền nhau: đổi vai trò thì yêu cầu địa bàn cũng đổi theo. */
  function wireRoleAndRegion(form, initialRegionId) {
    const roleSel = form.querySelector('[name="role"]');
    const regionHost = form.querySelector('[data-role="region-host"]');
    const hint = form.querySelector('[data-role="role-hint"]');
    const picker = createRegionPicker(regionHost, { value: initialRegionId ?? null, required: true });

    const sync = () => {
      const role = roleSel.value;
      hint.textContent = ROLE_HINT[role] || '';
      const needs = NEEDS_REGION.has(role);
      regionHost.hidden = !needs;
      form.querySelector('[data-role="region-note"]').hidden = needs;
    };
    roleSel.addEventListener('change', sync);
    sync();
    return { picker, roleSel };
  }

  /**
   * Ô chọn đơn vị — CHỈ admin hệ thống thấy.
   *
   * Quản trị đơn vị không cần (và không được) chọn: server lấy đơn vị của chính họ,
   * và gửi đơn vị khác vào đây là 422.
   */
  const instanceField = () =>
    laAdminHeThong
      ? `<div class="form-row" data-role="instance-host"><label>Đơn vị điều tra *</label>
           <select name="instanceId">
             <option value="">— Chọn đơn vị —</option>
             ${donVis
               .filter((d) => d.isActive)
               .map((d) => `<option value="${esc(d.id)}">${esc(d.name)}</option>`)
               .join('')}
           </select>
           <p class="hint-text">Tài khoản chỉ thấy dữ liệu của đơn vị này. Không đổi được về sau.</p>
         </div>`
      : '';

  /** Vai trò admin hệ thống không thuộc đơn vị nào -> ẩn ô đơn vị khi chọn vai trò đó. */
  function wireInstanceField(form) {
    const host = form.querySelector('[data-role="instance-host"]');
    if (!host) return;
    const roleSel = form.querySelector('[name="role"]');
    const sync = () => {
      host.hidden = roleSel.value === 'admin';
    };
    roleSel.addEventListener('change', sync);
    sync();
  }

  const regionFields = (label) => `
    <div data-role="region-host"></div>
    <p class="hint-text" data-role="region-note" hidden>
      ${esc(label)} thấy dữ liệu ngoài phạm vi địa bàn nên không cần gán.
    </p>`;

  function openCreateDialog() {
    // `fields` do onMount gán, KHÔNG lấy từ giá trị trả về của openDialog: onMount chạy
    // *bên trong* openDialog, nên gán biến này từ cả hai chỗ thì giá trị trả về (chạy
    // sau) sẽ ghi đè và `picker` biến mất — đúng lỗi "bấm Lưu không có tác dụng".
    let fields = null;
    openDialog({
      title: 'Tạo tài khoản',
      submitLabel: 'Tạo tài khoản',
      bodyHtml: `
        <div class="form-row"><label>Họ tên *</label>
          <input name="fullName" autocomplete="off" required></div>
        <div class="form-row"><label>Tên đăng nhập *</label>
          <input name="username" autocomplete="off" required>
          <p class="hint-text">Chữ thường không dấu, số, dấu chấm/gạch. Không đổi được sau khi tạo.</p></div>
        <div class="form-row"><label>Mật khẩu *</label>
          <input name="password" type="password" autocomplete="new-password" required>
          <p class="hint-text">Tối thiểu 8 ký tự. Báo trực tiếp cho người dùng — hệ thống không hiện lại được.</p></div>
        <div class="form-row"><label>Vai trò *</label>
          <select name="role">${roleOptions('enumerator', vaiTroCapDuoc)}</select>
          <p class="hint-text" data-role="role-hint"></p></div>
        ${instanceField()}
        ${regionFields('Vai trò này')}`,
      onMount: (form) => {
        fields = wireRoleAndRegion(form, null);
        wireInstanceField(form);
      },
      onSubmit: async (form) => {
        const fullName = form.fullName.value.trim();
        const username = form.username.value.trim().toLowerCase();
        const password = form.password.value;
        const role = form.role.value;
        const regionId = NEEDS_REGION.has(role) ? fields.picker.getValue() : undefined;
        // Admin hệ thống không thuộc đơn vị nào nên không có gì để suy ra: server trả
        // 422 nếu thiếu. Chặn trước ở đây để người dùng biết ngay phải làm gì.
        const instanceId = laAdminHeThong && role !== 'admin' ? form.instanceId?.value : undefined;

        if (!fullName) return 'Chưa nhập họ tên.';
        if (laAdminHeThong && role !== 'admin' && !instanceId) {
          return 'Chọn đơn vị điều tra cho tài khoản này.';
        }
        if (!/^[a-z0-9._-]{3,50}$/.test(username)) {
          return 'Tên đăng nhập chỉ gồm chữ thường không dấu, số, dấu chấm/gạch (3–50 ký tự).';
        }
        if (password.length < 8) return 'Mật khẩu phải có ít nhất 8 ký tự.';
        if (NEEDS_REGION.has(role) && !regionId) return 'Vai trò này bắt buộc phải gán địa bàn phụ trách.';

        try {
          await api.users.create({ fullName, username, password, role, regionId, instanceId });
          toast(`Đã tạo tài khoản "${username}"`);
          await load();
        } catch (err) {
          return err.message;
        }
      },
    });
  }

  function openEditDialog(user) {
    const self = user.id === me.id;
    let fields = null; // xem ghi chú ở openCreateDialog
    openDialog({
      title: `Sửa tài khoản: ${user.username}`,
      submitLabel: 'Lưu thay đổi',
      bodyHtml: `
        <div class="form-row"><label>Họ tên *</label>
          <input name="fullName" value="${esc(user.fullName)}" required></div>
        <div class="form-row"><label>Tên đăng nhập</label>
          <input value="${esc(user.username)}" disabled>
          <p class="hint-text">Tên đăng nhập gắn với lịch sử thao tác nên không đổi được.</p></div>
        ${
          user.instanceName
            ? `<div class="form-row"><label>Đơn vị điều tra</label>
                 <input value="${esc(user.instanceName)}" disabled>
                 <p class="hint-text">Không chuyển được người giữa hai đơn vị: lịch sử phiếu họ đã
                    nhập phải nằm lại đơn vị đã thực hiện. Chuyển người = khoá tài khoản cũ +
                    tạo tài khoản mới.</p></div>`
            : ''
        }
        <div class="form-row"><label>Vai trò *</label>
          <select name="role" ${self ? 'disabled' : ''}>${roleOptions(user.role, vaiTroCapDuoc)}</select>
          <p class="hint-text" data-role="role-hint"></p>
          ${self ? '<p class="hint-text">Không tự bỏ quyền quản trị của chính mình được.</p>' : ''}</div>
        ${regionFields('Vai trò này')}`,
      onMount: (form) => {
        fields = wireRoleAndRegion(form, user.regionId);
      },
      onSubmit: async (form) => {
        const fullName = form.fullName.value.trim();
        const role = self ? user.role : form.role.value;
        const regionId = NEEDS_REGION.has(role) ? fields.picker.getValue() : null;

        if (!fullName) return 'Chưa nhập họ tên.';
        if (NEEDS_REGION.has(role) && !regionId) return 'Vai trò này bắt buộc phải gán địa bàn phụ trách.';

        const payload = { fullName, regionId: regionId ?? null };
        if (!self) payload.role = role;
        try {
          await api.users.update(user.id, payload);
          const permissionChanged = role !== user.role || (regionId ?? null) !== user.regionId;
          toast(permissionChanged ? 'Đã lưu — người dùng cần đăng nhập lại' : 'Đã lưu thay đổi');
          await load();
        } catch (err) {
          return err.message;
        }
      },
    });
  }

  function openPasswordDialog(user) {
    openDialog({
      title: `Đặt lại mật khẩu: ${user.username}`,
      submitLabel: 'Đặt lại mật khẩu',
      bodyHtml: `
        <div class="form-row"><label>Mật khẩu mới *</label>
          <input name="password" type="password" autocomplete="new-password" required></div>
        <div class="form-row"><label>Nhập lại mật khẩu mới *</label>
          <input name="confirm" type="password" autocomplete="new-password" required></div>
        <p class="hint-text">Tối thiểu 8 ký tự. Mọi phiên đăng nhập hiện có của
           <b>${esc(user.fullName)}</b> sẽ bị thu hồi ngay.</p>`,
      onSubmit: async (form) => {
        const password = form.password.value;
        if (password.length < 8) return 'Mật khẩu phải có ít nhất 8 ký tự.';
        if (password !== form.confirm.value) return 'Hai lần nhập mật khẩu không khớp.';
        try {
          const res = await api.users.resetPassword(user.id, password);
          toast(`Đã đặt lại mật khẩu${res?.revokedSessions ? ` và thu hồi ${res.revokedSessions} phiên đăng nhập` : ''}`);
          await load();
        } catch (err) {
          return err.message;
        }
      },
    });
  }

  container.querySelector('#uNewBtn').addEventListener('click', openCreateDialog);
  [searchInput, roleSelect, statusSelect].forEach((elm) => {
    elm.addEventListener('change', load);
  });
  searchInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') load();
  });

  await load();
}
